/** Pure fade / duck math driven by the fixed step (unit-tested). */
export class Fader {
  value: number;
  target: number;
  private rate = 0; // per ms
  constructor(initial = 0) { this.value = initial; this.target = initial; }
  /** Move towards `target` over `ms` (0 = instantly). */
  to(target: number, ms: number): this {
    this.target = target;
    this.rate = ms <= 0 ? Infinity : Math.abs(target - this.value) / ms;
    if (ms <= 0) this.value = target;
    return this;
  }
  /** Advance by dtMs; returns true when the target has been reached this step or earlier. */
  step(dtMs: number): boolean {
    if (this.value === this.target) return true;
    const d = this.target - this.value;
    const move = this.rate * dtMs;
    if (Math.abs(d) <= move || !Number.isFinite(this.rate)) { this.value = this.target; return true; }
    this.value += Math.sign(d) * move;
    return false;
  }
  get done(): boolean { return this.value === this.target; }
}

/** Equal-power crossfade gains at t in [0,1]: [outgoing, incoming]. */
export function crossfadeGains(t: number): [number, number] {
  const c = Math.min(1, Math.max(0, t)) * Math.PI * 0.5;
  return [Math.cos(c), Math.sin(c)];
}

/**
 * Ducking envelope: attack to (1 - amount) over `attackMs`, hold until `holdMs` has elapsed, release
 * back to 1 over `releaseMs`. Pure function of elapsed time so it is trivially deterministic.
 */
export function duckGain(elapsedMs: number, amount: number, holdMs: number, attackMs = 120, releaseMs = 450): number {
  const a = Math.min(1, Math.max(0, amount));
  if (elapsedMs <= 0) return 1;
  if (elapsedMs < attackMs) return 1 - a * (elapsedMs / attackMs);
  if (elapsedMs < attackMs + holdMs) return 1 - a;
  const r = (elapsedMs - attackMs - holdMs) / Math.max(1, releaseMs);
  return r >= 1 ? 1 : 1 - a * (1 - r);
}
